import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { providerAPI, healthAPI, analyticsAPI } from '../../services/api';

const STATUS_COLORS = {
  PENDING_QUOTE: '#f59e0b',
  QUOTED: '#3b82f6',
  QUOTE_CONFIRMED: '#8b5cf6',
  BOOKED: '#10b981',
  IN_TRANSIT: '#06b6d4',
  DELIVERED: '#16a34a'
};

const STATUS_LABELS = {
  PENDING_QUOTE: 'Pending Quote',
  QUOTED: 'Quoted',
  QUOTE_CONFIRMED: 'Confirmed',
  BOOKED: 'Booked',
  IN_TRANSIT: 'In Transit',
  DELIVERED: 'Delivered'
};

const Dashboard = () => {
  const [health, setHealth] = useState(null);
  const [providers, setProviders] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [savings, setSavings] = useState(null);
  const [taskSummary, setTaskSummary] = useState(null);
  const [redFlags, setRedFlags] = useState([]);
  const [staleProviders, setStaleProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const results = await Promise.allSettled([
          healthAPI.check(),
          providerAPI.getAll({ status: 'ACTIVE' }),
          analyticsAPI.getPipeline(),
          analyticsAPI.getSavings(),
          analyticsAPI.getTaskSummary(),
          analyticsAPI.getRedFlags(),
          analyticsAPI.getStaleProviders()
        ]);

        if (results[0].status === 'fulfilled') setHealth(results[0].value);
        if (results[1].status === 'fulfilled') setProviders(results[1].value.data || []);
        if (results[2].status === 'fulfilled') setPipeline(results[2].value.data);
        if (results[3].status === 'fulfilled') setSavings(results[3].value.data);
        if (results[4].status === 'fulfilled') setTaskSummary(results[4].value.data);
        if (results[5].status === 'fulfilled') setRedFlags(results[5].value.data || []);
        if (results[6].status === 'fulfilled') setStaleProviders(results[6].value.data || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  const topProviders = providers
    .sort((a, b) => b.reliabilityScore - a.reliabilityScore)
    .slice(0, 5);

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
      </div>

      <div className="dashboard-grid">
        {/* Pipeline Cards */}
        {pipeline && (
          <div className="dashboard-card full-width">
            <h2>Shipment Pipeline</h2>
            <div className="pipeline-cards">
              {Object.entries(pipeline.pipeline).map(([status, count]) => (
                <Link
                  key={status}
                  to={`/shipments?status=${status}`}
                  className="pipeline-card"
                  style={{ borderTopColor: STATUS_COLORS[status] }}
                >
                  <div className="pipeline-count">{count}</div>
                  <div className="pipeline-label">{STATUS_LABELS[status]}</div>
                </Link>
              ))}
              <div className="pipeline-card pipeline-total">
                <div className="pipeline-count">{pipeline.total}</div>
                <div className="pipeline-label">Total</div>
              </div>
            </div>
          </div>
        )}

        {/* Task Alerts */}
        {taskSummary && (
          <div className="dashboard-card">
            <h2>Task Alerts</h2>
            <div className="stat-grid">
              <div className="stat-item">
                <div className="stat-value">{taskSummary.totalOpen}</div>
                <div className="stat-label">Open Tasks</div>
              </div>
              <div className="stat-item">
                <div className="stat-value" style={{ color: taskSummary.overdue > 0 ? 'var(--danger-color)' : undefined }}>
                  {taskSummary.overdue}
                </div>
                <div className="stat-label">Overdue</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{taskSummary.byPriority.HIGH || 0}</div>
                <div className="stat-label">High Priority</div>
              </div>
            </div>
            <div className="card-footer">
              <Link to="/tasks" className="link">View all tasks →</Link>
            </div>
          </div>
        )}

        {/* Cost Savings */}
        {savings && (
          <div className="dashboard-card">
            <h2>Cost Savings</h2>
            <div className="stat-grid">
              <div className="stat-item">
                <div className="stat-value savings-value">
                  ${savings.totalSavings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <div className="stat-label">Total Savings</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">
                  ${savings.avgSavings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <div className="stat-label">Avg per Comparison</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{savings.totalComparisons}</div>
                <div className="stat-label">Comparisons Made</div>
              </div>
            </div>
          </div>
        )}

        {/* System Status */}
        {health && (
          <div className="dashboard-card">
            <h2>System Status</h2>
            <div className="status-grid">
              <div className="status-item">
                <label>API Status</label>
                <span className={`status-badge status-${health.status}`}>{health.status}</span>
              </div>
              <div className="status-item">
                <label>Database</label>
                <span className={`status-badge status-${health.database}`}>{health.database}</span>
              </div>
              <div className="status-item">
                <label>Uptime</label>
                <span>{(health.uptime / 60).toFixed(1)} minutes</span>
              </div>
            </div>
          </div>
        )}

        {/* Provider Overview */}
        <div className="dashboard-card">
          <h2>Provider Overview</h2>
          <div className="stat-grid">
            <div className="stat-item">
              <div className="stat-value">{providers.length}</div>
              <div className="stat-label">Active Providers</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {providers.reduce((sum, p) => sum + p.quotesRequested, 0)}
              </div>
              <div className="stat-label">Quotes Requested</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {providers.reduce((sum, p) => sum + p.bookingsCompleted, 0)}
              </div>
              <div className="stat-label">Bookings</div>
            </div>
          </div>
          <div className="card-footer">
            <Link to="/providers" className="link">View all providers →</Link>
          </div>
        </div>

        {/* Top Providers */}
        <div className="dashboard-card full-width">
          <h2>Top Providers by Reliability</h2>
          {topProviders.length === 0 ? (
            <div className="empty-state">
              <p>No active providers yet</p>
              <Link to="/providers/new" className="btn btn-primary">
                Add Your First Provider
              </Link>
            </div>
          ) : (
            <table className="simple-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Provider</th>
                  <th>Type</th>
                  <th>Reliability</th>
                  <th>Response Time</th>
                  <th>Quote Rate</th>
                  <th>Bookings</th>
                </tr>
              </thead>
              <tbody>
                {topProviders.map((provider, index) => (
                  <tr key={provider.id}>
                    <td>{index + 1}</td>
                    <td>
                      <Link to={`/providers/${provider.id}`}>{provider.name}</Link>
                    </td>
                    <td>
                      <span className={`badge badge-${provider.type.toLowerCase()}`}>
                        {provider.type}
                      </span>
                    </td>
                    <td>
                      <div className="reliability-indicator">
                        <span>{Math.min(100, Math.max(0, Number(provider.reliabilityScore) * 100)).toFixed(0)}%</span>
                        <div className="mini-bar">
                          <div
                            className="mini-fill"
                            style={{ width: `${Math.min(100, Math.max(0, Number(provider.reliabilityScore) * 100))}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>{Number(provider.averageResponseTime).toFixed(1)}h</td>
                    <td>
                      {provider.quotesRequested > 0
                        ? `${Math.min(100, Math.max(0, (provider.quotesReceived / provider.quotesRequested) * 100)).toFixed(0)}%`
                        : 'N/A'}
                    </td>
                    <td>{provider.bookingsCompleted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Alerts Section */}
        {(redFlags.length > 0 || staleProviders.length > 0) && (
          <div className="dashboard-card full-width">
            <h2>Provider Alerts</h2>
            {redFlags.length > 0 && (
              <div className="alert-section">
                <h3 className="alert-title alert-danger">Red Flags ({redFlags.length})</h3>
                <div className="alert-list">
                  {redFlags.map(p => (
                    <div key={p.id} className="alert-item alert-item-danger">
                      <Link to={`/providers/${p.id}`} className="alert-provider-name">{p.name}</Link>
                      <span className="alert-reasons">
                        {p.reasons.join(' | ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {staleProviders.length > 0 && (
              <div className="alert-section">
                <h3 className="alert-title alert-warning">Stale Providers ({staleProviders.length})</h3>
                <div className="alert-list">
                  {staleProviders.slice(0, 5).map(p => (
                    <div key={p.id} className="alert-item alert-item-warning">
                      <Link to={`/providers/${p.id}`} className="alert-provider-name">{p.name}</Link>
                      <span className="text-muted">{p.daysSinceActivity} days inactive</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="dashboard-card">
          <h2>Quick Actions</h2>
          <div className="action-list">
            <Link to="/providers/new" className="action-item">
              <span className="action-icon">+</span>
              <span>Add New Provider</span>
            </Link>
            <Link to="/shipments" className="action-item">
              <span className="action-icon">S</span>
              <span>View Shipments</span>
            </Link>
            <Link to="/tasks" className="action-item">
              <span className="action-icon">T</span>
              <span>View Tasks</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
